import type { CSSObject } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

import {
  KESTRIX_CHECKMARK_PATH,
  kestrixSvgBackgroundImage,
} from '../../KestrixSvgIcons.jsx';
import { colorTokens } from '../../tokens/colors.ts';

/** Outer checkbox box size. */
const BOX_SIZE = 20;
/** Outer checkbox border radius. */
const BOX_RADIUS = 6;
/** Inner glyph size inside the box. */
const INNER_SIZE = BOX_SIZE - 4;

/** Fill for checked and indeterminate icons. */
const activeFillStyles: CSSObject = {
  backgroundColor: colorTokens.checkbox.active.surface,
  boxShadow: 'none',
};

/** Shared hover/disabled states for active checkbox variants. */
const activeStateStyles: CSSObject = {
  'input:hover ~ &': {
    backgroundColor: colorTokens.checkbox.active.hoverSurface,
    boxShadow: 'none',
  },
  'input:disabled ~ &': {
    backgroundColor: colorTokens.checkbox.active.disabledSurface,
    boxShadow: `inset 0 0 0 1px ${colorTokens.checkbox.active.disabledSurface}`,
  },
};

/** Shared pseudo-element icon styles for checked checkbox states. */
const innerIconStyles = (
  path: string,
): CSSObject => ({
  '&::before': {
    display: 'block',
    width: INNER_SIZE,
    height: INNER_SIZE,
    backgroundImage: kestrixSvgBackgroundImage(
      path,
      colorTokens.checkbox.active.glyph,
    ),
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    content: '""',
  },
});

const minusBarStyles: CSSObject = {
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 10,
    height: 2,
    borderRadius: 1,
    backgroundColor: colorTokens.checkbox.active.glyph,
    transform: 'translate(-50%, -50%)',
  },
};

/** Default (unchecked) checkbox icon. */
export const KestrixCheckboxItemDefault = styled('span')(() => ({
  borderRadius: BOX_RADIUS,
  width: BOX_SIZE,
  height: BOX_SIZE,
  padding: 2,
  display: 'inline-block',
  boxSizing: 'border-box',
  boxShadow: `inset 0 0 0 1px ${colorTokens.checkbox.unchecked.border}`,
  backgroundColor: colorTokens.checkbox.unchecked.surface,
  transition: 'background-color 150ms ease, box-shadow 150ms ease',
  '.Mui-focusVisible &': {
    outline: `3px auto ${colorTokens.checkbox.focusRing}`,
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: colorTokens.checkbox.unchecked.hoverSurface,
    boxShadow: `inset 0 0 0 1px ${colorTokens.checkbox.unchecked.hoverBorder}`,
  },
  'input:disabled ~ &': {
    backgroundColor: colorTokens.checkbox.unchecked.disabledSurface,
    boxShadow: `inset 0 0 0 1px ${colorTokens.checkbox.unchecked.disabledBorder}`,
  },
}));


/** Checked checkbox icon. */
export const KestrixCheckboxItemChecked = styled(KestrixCheckboxItemDefault)({
  ...activeFillStyles,
  ...innerIconStyles(KESTRIX_CHECKMARK_PATH),
  ...activeStateStyles,
});

/** Indeterminate checkbox icon (minus bar). */
export const KestrixCheckboxItemIndeterminate = styled(KestrixCheckboxItemDefault)({
  ...activeFillStyles,
  ...minusBarStyles,
  ...activeStateStyles,
});
